import { styled, Theme } from "@mui/material/styles";
import { Box, Button, Typography } from "@mui/material";

type ContainerProps = {
  isAI: boolean;
  messageIndex: number;
};

export const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI" && prop !== "messageIndex",
})(({ isAI, messageIndex }: ContainerProps) => ({
  display: "flex",
  flexDirection: isAI ? "row" : "row-reverse",
  alignItems: "flex-start",
  width: "100%",
  marginTop: messageIndex === 0 ? "auto" : 0,
}));

type AvatarContainerProps = {
  isAI: boolean;
};

export const AvatarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI",
})(({ theme, isAI }: AvatarContainerProps & { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  borderRadius: "4px",
  backgroundColor: isAI ? "transparent" : "#555555",
  flexShrink: 0,
  marginTop: "2px",
}));

type MessageContainerProps = {
  theme: Theme;
  isAI: boolean;
};

export const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI",
})(({ theme, isAI }: MessageContainerProps) => ({
  backgroundColor: isAI
    ? theme.palette.background.default
    : theme.palette.inputBackground,
  borderRadius: "12px",
  border: isAI ? `1px solid ${theme.palette.divider}` : "none",
  textAlign: `${isAI ? "left" : "right"}`,
  padding: "12px 16px",
  maxWidth: "75%",
  minWidth: 0,
  display: "flex",
  overflow: "hidden",
}));

type MessageBoxProps = {
  isAI: boolean;
};

export const MessageBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI",
})(({ theme, isAI }: MessageBoxProps & { theme: Theme }) => ({
  color: isAI ? theme.palette.text.primary : theme.palette.text.secondary,
  fontSize: "14px",
  lineHeight: 1.5,
  minWidth: 0,
  width: "100%",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  "& p": {
    margin: 0,
  },
  "& p + p": {
    marginTop: "8px",
  },
  "& a": {
    color: theme.palette.primary.main,
    wordBreak: "break-all",
  },
  "& pre": {
    maxWidth: "100%",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: "8px 0",
  },
  "& code": {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
  },
  "& ul, & ol": {
    paddingLeft: "20px",
    margin: "4px 0",
  },
  "& table": {
    display: "block",
    width: "100%",
    overflowX: "auto",
    borderCollapse: "collapse",
    margin: "8px 0",
    fontSize: "13px",
  },
  "& th, & td": {
    border: `1px solid ${theme.palette.divider}`,
    padding: "6px 10px",
    textAlign: "left",
    verticalAlign: "top",
  },
  "& th": {
    fontWeight: 600,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const MessageMeta = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI",
})(({ isAI }: { isAI: boolean }) => ({
  display: "flex",
  flexDirection: isAI ? "row" : "row-reverse",
  alignItems: "center",
  flexWrap: "wrap",
  rowGap: "4px",
  columnGap: "10px",
  marginTop: "4px",
  paddingLeft: isAI ? "46px" : "0",
  paddingRight: isAI ? "0" : "46px",
}));

export const Timestamp = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  fontSize: "11px",
}));

export const CheckMark = styled(Box)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  display: "inline-flex",
  alignItems: "center",
  fontSize: "11px",
}));

export const SuggestedQuestionsRow = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "10px",
  paddingLeft: "46px",
}));

export const SuggestedQuestionChip = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: 1.4,
  padding: "6px 14px",
  minWidth: "auto",
  maxWidth: "100%",
  whiteSpace: "normal",
  textAlign: "left",
  justifyContent: "flex-start",
  gap: "6px",
  transition:
    "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease",
  "& .MuiButton-startIcon": {
    marginRight: "2px",
    marginLeft: 0,
    color: theme.palette.primary.main,
    transition: "transform 0.18s ease",
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${theme.palette.primary.main}1f`,
    "& .MuiButton-startIcon": {
      transform: "rotate(-8deg) scale(1.1)",
    },
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    opacity: 0.7,
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  },
}));

export const RateExperienceRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  rowGap: "2px",
  columnGap: "10px",
  padding: 0,
}));

export const RateExperienceLink = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "12px",
  padding: 0,
  minWidth: "auto",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
}));

export const WelcomeMessageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  width: "100%",
  paddingLeft: "30px",
  paddingRight: "30px",
}));
